import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { COMING_SOON_MODE } from '../comingSoonConfig'


// ────────────────────────────────────────────────────────────────────────
//  Home — JASPR Market landing page (v26 redesign, May 2026)
//
//  Soft-launch homepage for jasprmarket.com.
//  - Top: dismissible "Coming Soon" banner
//  - Hero: dark gradient with headline + CTAs + feature stack
//  - Trust strip: 5 promises
//  - Category grid: 8 categories with Lucide-style SVG icons
//  - Receives `t` and `language` props from App.js (matches existing pattern)
//  - RTL-aware via dir attribute on root
//
//  Image swap-out points are marked with REPLACE_WITH_PHOTO comments.
// ────────────────────────────────────────────────────────────────────────

function Home({ t = (k) => k, language = 'EN' }) {
  const isRTL = language === 'AR'
  const [showBanner, setShowBanner] = useState(false)
  const isMobile = window.innerWidth <= 768

  // Show the banner by default while in Coming Soon mode, unless the
  // person already dismissed it in this browser.
  useEffect(() => {
    if (!COMING_SOON_MODE) { setShowBanner(false); return }
    const dismissed = localStorage.getItem('mm_comingsoon_dismissed')
    setShowBanner(dismissed !== 'v1')
  }, [])

  const dismissBanner = () => {
    setShowBanner(false)
    localStorage.setItem('mm_comingsoon_dismissed', 'v1')
  }



  // Categories — 7 parent buckets matching the DB category tree.
  // `comingSoon: true` shows an overlay on the card and keeps it disabled
  // until we add products to that bucket.
const categories = [
  {
    id: 'mobiles',
    label: t('mobiles'),
    image: '/categories/mobiles.png',
    to: '/products?category=Mobiles'
  },
  {
    id: 'computers',
    label: t('computers'),
    image: '/categories/computers.png',
    to: '/products?category=Computers'
  },
  {
    id: 'audio',
    label: t('audio'),
    image: '/categories/audio.png',
    to: '/products?category=Audio'
  },
  {
    id: 'wearables',
    label: t('wearables'),
    image: '/categories/wearables.png',
    to: '/products?category=Wearables'
  },
  {
    id: 'accessories',
    label: t('accessories') !== 'accessories' ? t('accessories') : 'Accessories',
    image: '/categories/accessories.png',
    fallbackImage: '/categories/mobiles.png',
    to: '/products?category=Accessories'
  },
  {
    id: 'fashion',
    label: t('fashion'),
    image: '/categories/fashion.png',
    to: '/products?category=Fashion',
    comingSoon: true
  },
  {
    id: 'beauty',
    label: t('beauty'),
    image: '/categories/beauty.png',
    to: '/products?category=Beauty',
    comingSoon: true
  },
  {
    id: 'home',
    label: t('homeKitchen'),
    image: '/categories/home-kitchen.png',
    to: '/products?category=Home%20%26%20Kitchen',
    comingSoon: true
  },
  {
    id: 'kids',
    label: language === 'AR' ? 'الأطفال' : 'Kids',
    image: '/categories/kids.png',
    to: '/products?category=Kids',
    comingSoon: true
  },
  {
    id: 'perfumes',
    label: language === 'AR' ? 'العطور' : 'Perfumes',
    image: '/categories/perfumes.png',
    to: '/products?category=Perfumes',
    comingSoon: true
  }
]
  return (
    <div style={s.page} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* Coming Soon banner */}
      {showBanner && (
        <div style={s.banner}>
          <div style={s.bannerInner}>
            <span style={s.bannerEmoji}>🚀</span>
            <span style={s.bannerText}>{t('homeBannerText')}</span>
            <button onClick={dismissBanner} style={s.bannerClose} aria-label="Dismiss">×</button>
          </div>
        </div>
      )}


      {/* Hero section */}
      <section style={isMobile ? {...s.hero, ...s.heroMobile} : s.hero}>
        {/* Atmospheric background — Doha skyline silhouette */}
        <div style={s.heroBg}>
          <SkylineSVG />
        </div>

        <div style={isMobile ? {...s.heroInner, ...s.heroInnerMobile} : s.heroInner}>
          {/* Left — copy and CTAs */}
          <div style={s.heroLeft}>
            {!isMobile && (
  <div style={s.heroEyebrow}>{t('homeHeroEyebrow')}</div>
)}
            <h1 style={isMobile ? {...s.heroTitle, ...s.heroTitleMobile} : s.heroTitle}>
  {t('homeHeroTitle')}
</h1>
            <p style={isMobile ? {...s.heroSub, ...s.heroSubMobile} : s.heroSub}>{t('homeHeroSub')}</p>

            {/* Inline trust pills */}
            {!isMobile && (
  <div style={s.heroPills}>
    <Pill label={t('homeTrustVerified')} />
    <Pill label={t('homeTrustQuality')} />
    <Pill label={t('homeTrustSecure')} />
    <Pill label={t('homeTrustFast')} />
  </div>
)}

            {/* CTAs */}
            <div style={isMobile ? {...s.heroCTAs, ...s.heroCTAsMobile} : s.heroCTAs}>
              <Link to="/products" style={isMobile ? {...s.ctaPrimary, ...s.ctaMobile} : s.ctaPrimary}>
                {t('homeShopNow')}
                <span style={s.ctaArrow}>→</span>
              </Link>
              <Link to="/register" style={isMobile ? {...s.ctaSecondary, ...s.ctaMobileSecondary} : s.ctaSecondary}>
                {t('homeSellDevice')}
              </Link>
            </div>
          </div>


          {/* Right — product showcase + feature cards */}
          <div style={isMobile ? {...s.heroRight, ...s.heroRightMobile} : s.heroRight}>
            {/* Phone graphic (replace with real product photo when available) */}
            <div style={s.phoneShowcase}>
              {/* REPLACE_WITH_PHOTO: drop a 600x600 product image here */}
              <PhoneSVG />
            </div>

            {/* Feature cards stacked */}
            <div style={s.featureStack}>
              <FeatureCard icon="🚚" title={t('homeFeatSameDayTitle')} sub={t('homeFeatSameDaySub')} />
              <FeatureCard icon="💵" title={t('homeFeatCODTitle')}     sub={t('homeFeatCODSub')} />
              <FeatureCard icon="↩"  title={t('homeFeatReturnTitle')}  sub={t('homeFeatReturnSub')} />
              <FeatureCard icon="🕐" title={t('homeFeatSupportTitle')} sub={t('homeFeatSupportSub')} />
            </div>
          </div>
        </div>
      </section>


      {/* Shop by Category */}
      <section style={isMobile ? {...s.section, ...s.sectionMobile} : s.section}>
        <div style={isMobile ? {...s.sectionHead, ...s.sectionHeadMobile} : s.sectionHead}>
          <h2 style={isMobile ? {...s.sectionTitle, ...s.sectionTitleMobile} : s.sectionTitle}>{t('homeCategoryTitle')}</h2>
          <Link to="/products" style={isMobile ? {...s.sectionLink, ...s.sectionLinkMobile} : s.sectionLink}>
            {t('homeViewAllCats')} <span>→</span>
          </Link>
        </div>
        <div style={isMobile ? {...s.catGrid, ...s.catGridMobile} : s.catGrid}>
       {categories.map(cat => {
  const baseCardStyle = isMobile ? { ...s.catCard, ...s.catCardMobile } : s.catCard
  const cardStyle = cat.comingSoon
    ? { ...baseCardStyle, position: 'relative', opacity: 0.85, cursor:'not-allowed' }
    : { ...baseCardStyle, position: 'relative' }

  return (
    <Link
  to={cat.comingSoon ? '#' : cat.to}
  key={cat.id}
  style={cardStyle}
  onClick={cat.comingSoon ? (e) => e.preventDefault() : undefined}
  aria-disabled={cat.comingSoon ? 'true' : undefined}

  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-6px)'
    e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.12)'
  }}

  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)'
    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)'
  }}
>
      <div style={s.catImageWrap}>
        <img
  src={cat.image}
  alt={cat.label}
  onError={(e) => {
    if (cat.fallbackImage && e.currentTarget.src.indexOf(cat.fallbackImage) === -1) {
      e.currentTarget.src = cat.fallbackImage
    }
  }}
  style={{
    width: '100%',
    height: isMobile ? 70 : 105,
    objectFit: 'contain',
    marginBottom: 8,
    transition: 'transform 0.3s ease',
    filter: cat.comingSoon ? 'grayscale(20%)' : 'none',
  }}
/>
      </div>
                <div style={s.catLabel}>{cat.label}</div>
                {cat.comingSoon && (
                  <span style={{
                    position:'absolute',
                    top:8,
                    right:8,
                    background:'#f97316',
                    color:'#fff',
                    fontSize:9,
                    fontWeight:700,
                    padding:'3px 7px',
                    borderRadius:10,
                    letterSpacing:0.4,
                    textTransform:'uppercase',
                    boxShadow:'0 2px 6px rgba(249,115,22,0.35)'
                  }}>
                    {t('comingSoon')}
                  </span>
                )}
              </Link>
            )
          })}
        </div>
      </section>

      {/* Popular Products teaser → links into Products page */}
      <section style={{...s.section, paddingBottom: 80}}>
        <div style={s.sectionHead}>
          <h2 style={s.sectionTitle}>{t('homePopularTitle')}</h2>
          <Link to="/products" style={s.sectionLink}>
            {t('homeViewAllProducts')} <span>→</span>
          </Link>
        </div>
        <div style={s.popularTeaser}>
          <div style={s.popularInner}>
            <p style={s.popularText}>{t('homePopularComingSoon')}</p>
            <Link to="/products" style={s.ctaPrimary}>
              {t('homeBrowseProducts')}
              <span style={s.ctaArrow}>→</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────
//  Small building-block components
// ────────────────────────────────────────────────────────────────────────

const Pill = ({ label }) => (
  <span style={s.pill}>
    <span style={s.pillDot}>●</span> {label}
  </span>
)

const FeatureCard = ({ icon, title, sub }) => (
  <div style={s.featCard}>
    <div style={s.featIcon}>{icon}</div>
    <div>
      <div style={s.featTitle}>{title}</div>
      <div style={s.featSub}>{sub}</div>
    </div>
  </div>
)

const TrustItem = ({ icon: Icon, title, sub }) => (
  <div style={s.trustItem}>
    <div style={s.trustIcon}><Icon /></div>
    <div>
      <div style={s.trustTitle}>{title}</div>
      <div style={s.trustSub}>{sub}</div>
    </div>
  </div>
)

// ────────────────────────────────────────────────────────────────────────
//  Inline SVG icons — Lucide-inspired, single-color, scale via parent
// ────────────────────────────────────────────────────────────────────────

const svgProps = {
  width: 32, height: 32, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round',
}

const IconPhone = () => (
  <svg {...svgProps}><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
)
const IconLaptop = () => (
  <svg {...svgProps}><rect x="2" y="4" width="20" height="13" rx="2"/><line x1="2" y1="20" x2="22" y2="20"/></svg>
)
const IconEarbuds = () => (
  <svg {...svgProps}><circle cx="6" cy="15" r="4"/><circle cx="18" cy="15" r="4"/><path d="M2 15v-2a10 10 0 0 1 20 0v2"/></svg>
)
const IconTablet = () => (
  <svg {...svgProps}><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
)
const IconWatch = () => (
  <svg {...svgProps}><circle cx="12" cy="12" r="6"/><path d="M12 10v2l1 1"/><path d="M16.5 4.5 18 3"/><path d="M7.5 4.5 6 3"/><path d="M16.5 19.5 18 21"/><path d="M7.5 19.5 6 21"/></svg>
)
const IconCharger = () => (
  <svg {...svgProps}><path d="M11 9 7.5 12.5 11 16"/><path d="m13 8 3.5 3.5L13 15"/><rect x="2" y="6" width="20" height="12" rx="2"/></svg>
)
const IconCase = () => (
  <svg {...svgProps}><rect x="6" y="2" width="12" height="20" rx="3"/><circle cx="12" cy="6" r="0.5"/></svg>
)
const IconHeadphones = () => (
  <svg {...svgProps}><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
)

const IconUser = () => (
  <svg {...svgProps}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
)
const IconCheck = () => (
  <svg {...svgProps}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
)
const IconLock = () => (
  <svg {...svgProps}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
)
const IconReturn = () => (
  <svg {...svgProps}><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-15-6.7L3 13"/></svg>
)
const IconTruck = () => (
  <svg {...svgProps}><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
)
const IconMonitor = () => (
  <svg {...svgProps}>
    <rect x="2" y="4" width="20" height="14" rx="2" />
    <line x1="8" y1="20" x2="16" y2="20" />
    <line x1="12" y1="18" x2="12" y2="20" />
  </svg>
)

const IconShirt = () => (
  <svg {...svgProps}>
    <path d="M8 4l4 2 4-2 4 4-3 3v9H7v-9L4 8l4-4z" />
  </svg>
)

const IconSparkles = () => (
  <svg {...svgProps}>
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
  </svg>
)

const IconHome = () => (
  <svg {...svgProps}>
    <path d="M4 10l8-6 8 6v10H4V10z" />
  </svg>
)

const IconShoppingCart = () => (
  <svg {...svgProps}>
    <circle cx="9" cy="20" r="1" />
    <circle cx="17" cy="20" r="1" />
    <path d="M5 5h2l2 10h8l2-6H8" />
  </svg>
)

const IconTrophy = () => (
  <svg {...svgProps}>
    <path d="M8 4h8v4a4 4 0 01-8 0V4z" />
    <path d="M6 4H4v2a4 4 0 004 4" />
    <path d="M18 4h2v2a4 4 0 01-4 4" />
    <line x1="12" y1="12" x2="12" y2="18" />
    <line x1="9" y1="20" x2="15" y2="20" />
  </svg>
)

// Hero phone showcase — graphic stand-in until real photo
const PhoneSVG = () => (
  <svg width="320" height="420" viewBox="0 0 280 380" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="phoneBody" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1f2937" />
        <stop offset="50%" stopColor="#0f172a" />
        <stop offset="100%" stopColor="#1e293b" />
      </linearGradient>
      <linearGradient id="phoneScreen" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.9" />
        <stop offset="50%" stopColor="#2563eb" stopOpacity="0.7" />
        <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0.9" />
      </linearGradient>
      <radialGradient id="glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#f97316" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
      </radialGradient>
    </defs>
    {/* Orange glow behind phone */}
    <circle cx="140" cy="200" r="160" fill="url(#glow)" />
    {/* Phone shadow */}
    <ellipse cx="140" cy="365" rx="80" ry="6" fill="#000" opacity="0.4" />
    {/* Phone body */}
    <rect x="70" y="40" width="140" height="300" rx="22" fill="url(#phoneBody)" stroke="#374151" strokeWidth="1" />
    {/* Screen */}
    <rect x="80" y="58" width="120" height="264" rx="14" fill="url(#phoneScreen)" />
    {/* Camera notch */}
    <rect x="125" y="50" width="30" height="6" rx="3" fill="#0f172a" />
    {/* Screen highlight */}
    <rect x="84" y="62" width="40" height="100" rx="10" fill="#ffffff" opacity="0.12" />
    {/* JASPR Market watermark on screen */}
   {/* App screen content */}
<text
  x="140"
  y="115"
  textAnchor="middle"
  fontSize="11"
  fill="#ffffff"
  fontWeight="700"
>
  JASPR MARKET
</text>

<text x="140" y="145" textAnchor="middle" fontSize="10" fill="#ffffff">
  Categories
</text>

<text x="100" y="175" textAnchor="middle" fontSize="13">📱</text>
<text x="140" y="175" textAnchor="middle" fontSize="13">💄</text>
<text x="180" y="175" textAnchor="middle" fontSize="13">🏠</text>
<text x="120" y="215" textAnchor="middle" fontSize="13">👗</text>
<text x="160" y="215" textAnchor="middle" fontSize="13">🎧</text>

<rect x="98" y="250" width="84" height="28" rx="8" fill="#f97316" />

<text
  x="140"
  y="268"
  textAnchor="middle"
  fontSize="10"
  fontWeight="700"
  fill="#ffffff"
>
  Shop Now
</text>
  </svg>
)

// Doha skyline silhouette behind hero (atmospheric)
const SkylineSVG = () => (
  <svg viewBox="0 0 1440 240" preserveAspectRatio="xMidYMax slice" width="100%" height="100%" aria-hidden="true">
    <defs>
      <linearGradient id="skyFade" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#0f172a" stopOpacity="0" />
        <stop offset="100%" stopColor="#0f172a" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    <path
      d="M0,240 L0,180 L60,180 L60,160 L100,160 L100,140 L130,140 L130,150 L170,150 L170,120 L200,120 L200,100 L230,100 L230,130 L280,130 L280,90 L310,90 L310,70 L340,70 L340,110 L380,110 L380,140 L420,140 L420,80 L460,80 L460,60 L490,60 L490,130 L530,130 L530,100 L570,100 L570,150 L610,150 L610,90 L650,90 L650,70 L680,70 L680,120 L720,120 L720,80 L750,80 L750,40 L780,40 L780,90 L820,90 L820,140 L860,140 L860,110 L890,110 L890,80 L930,80 L930,60 L960,60 L960,130 L1000,130 L1000,100 L1040,100 L1040,150 L1080,150 L1080,90 L1120,90 L1120,70 L1160,70 L1160,130 L1200,130 L1200,160 L1240,160 L1240,120 L1280,120 L1280,150 L1320,150 L1320,180 L1380,180 L1380,160 L1440,160 L1440,240 Z"
      fill="#1e293b"
      opacity="0.5"
    />
    <rect x="0" y="0" width="1440" height="240" fill="url(#skyFade)" />
  </svg>
)

// ────────────────────────────────────────────────────────────────────────
//  Styles
// ────────────────────────────────────────────────────────────────────────

const s = {
  page: {
    background: '#f7f8fa',
    color: '#0f172a',
    fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
    minHeight: '100vh',
  },

  // Banner
  banner: {
    background: 'linear-gradient(90deg, #f97316 0%, #fb923c 100%)',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 500,
    padding: '10px 0',
  },
  bannerInner: {
    maxWidth: 1280,
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    position: 'relative',
  },
  bannerEmoji: { fontSize: 18 },
  bannerText: { letterSpacing: '0.01em' },
  bannerClose: {
    position: 'absolute',
    right: 24,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.15)',
    color: '#ffffff',
    border: 'none',
    width: 26,
    height: 26,
    borderRadius: '50%',
    fontSize: 20,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    padding: 0,
  },

  
  // Hero
hero: {
  background: 'linear-gradient(135deg, #0f1923 0%, #1e3a5f 50%, #0f172a 100%)',
  color: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  padding: '0px 0 8px',
},
heroMobile: {
  padding: '0',
},
  heroBg: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 0.5,
  },
  heroInner: {
    maxWidth: 1280,
    margin: '0 auto',
    padding: '0 24px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    position: 'relative',
    zIndex: 1,
  },
  heroLeft: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  heroEyebrow: {
    color: '#f97316',
    fontSize: 13,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    marginBottom: 16,
  },
  heroTitle: {
    fontFamily: '"Inter Tight", system-ui, sans-serif',
    fontSize: 42,
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: '-0.03em',
    margin: '0 0 20px',
    color: '#ffffff',
  },
  heroSub: {
    fontSize: 17,
    lineHeight: 1.6,
    color: '#cbd5e1',
    maxWidth: 480,
    margin: '0 0 18px',
  },
  heroPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 36,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 999,
    fontSize: 12,
    color: '#e2e8f0',
    fontWeight: 500,
  },
  pillDot: { color: '#f97316', fontSize: 10 },
  heroCTAs: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  ctaPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    background: '#f97316',
    color: '#ffffff',
    padding: '14px 28px',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 15,
    transition: 'transform 0.15s, box-shadow 0.15s',
    boxShadow: '0 4px 14px rgba(249,115,22,0.35)',
  },
  ctaArrow: { fontSize: 18, fontWeight: 400 },
  ctaSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    background: 'transparent',
    color: '#ffffff',
    padding: '14px 28px',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 15,
    border: '1px solid rgba(255,255,255,0.25)',
  },

  heroRight: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 24,
    alignItems: 'center',
  },
  phoneShowcase: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  featCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: 'rgba(255,255,255,0.07)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.14)',
    padding: '14px 18px',
    borderRadius: 14,
    minWidth: 240,
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
  },
  featIcon: {
    fontSize: 22,
    width: 42,
    height: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(249,115,22,0.18)',
    border: '1px solid rgba(249,115,22,0.28)',
    borderRadius: 10,
    flexShrink: 0,
  },
  featTitle: {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  featSub: {
    color: '#cbd5e1',
    fontSize: 12,
    marginTop: 3,
    lineHeight: 1.4,
  },
  heroRightMobile: {
  display: 'none',
},

heroInnerMobile: {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '18px 20px 20px',
},

heroTitleMobile: {
  fontSize: 23,
  lineHeight: 1.12,
  margin: '0 0 12px',
},

heroSubMobile: {
  fontSize: 13.5,
  lineHeight: 1.45,
  margin: '0 0 14px',
  maxWidth: '100%',
},

heroCTAsMobile: {
  gap: 10,
  flexWrap: 'nowrap',
},

ctaMobile: {
  padding: '11px 18px',
  fontSize: 13,
  borderRadius: 8,
  flex: 1,
  justifyContent: 'center',
},

ctaMobileSecondary: {
  padding: '11px 16px',
  fontSize: 13,
  borderRadius: 8,
  flex: 1,
  justifyContent: 'center',
},

  // Trust strip — bridges hero to content
  trustStrip: {
    background: '#ffffff',
    marginTop: -32,
    position: 'relative',
    zIndex: 2,
  },
  trustInner: {
    maxWidth: 1280,
    margin: '0 auto',
    padding: '24px 32px',
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.05)',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 24,
  },
  trustItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  trustIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: '#fff7ed',
    color: '#f97316',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  trustTitle: {
    fontWeight: 600,
    fontSize: 14,
    color: '#0f1923',
    lineHeight: 1.3,
  },
  trustSub: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
  },

  // Section wrapper
  section: {
    maxWidth: 1280,
    margin: '0 auto',
    padding: '36px 24px 0',
  },
  sectionMobile: {
    padding: '26px 16px 0',
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 12,
  },
  sectionHeadMobile: {
    marginBottom: 16,
    gap: 6,
  },
  sectionTitle: {
    fontFamily: '"Inter Tight", system-ui, sans-serif',
    fontSize: 28,
    fontWeight: 700,
    color: '#0f1923',
    letterSpacing: '-0.02em',
    margin: 0,
  },
  sectionTitleMobile: {
    fontSize: 22,
    lineHeight: 1.15,
  },
  sectionLink: {
    color: '#f97316',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  sectionLinkMobile: {
    fontSize: 12,
  },

  // Category grid
  catGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 1fr)',
    gap: 10,
  },
  catGridMobile: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
  },
catCard: {
  background: 'linear-gradient(180deg,#ffffff 0%,#fafafa 100%)',
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  padding: '14px 8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  textDecoration: 'none',
  color: '#0f1923',
  transition: 'all .25s ease',
  minHeight: 150,
  boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
  overflow: 'hidden',
  cursor: 'pointer',
},
catCardMobile: {
  minHeight: 126,
  padding: '12px 10px',
  borderRadius: 16,
  gap: 8,
  boxShadow: '0 6px 16px rgba(15,23,42,0.07)',
},
catImageWrap: {
  width: 80,
  height: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 8,
},
  catLabel: {
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'center',
    color: '#0f1923',
  },

  // Popular products teaser
  popularTeaser: {
    background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
    border: '1px solid #fed7aa',
    borderRadius: 16,
    padding: '40px 24px',
    textAlign: 'center',
  },
  popularInner: {
    maxWidth: 480,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
  },
  popularText: {
    color: '#9a3412',
    fontSize: 15,
    margin: 0,
    lineHeight: 1.5,
  },
}

export default Home
